import {
  AsyncLoadingSection,
  buildEndpoint,
  ColumnLayout,
  defaultDataTransform,
  DisplayBlock,
  DropdownItemModel,
  InputType,
  PageCard,
  PageCardRowSection,
  Render,
  SmartInputBlock,
  TaskPageHeaderLayout,
  useAddEditSuccessHandler,
  useDataFetcher,
  useEnums,
  useIdParam,
  useToasts,
} from '@techsharegov/react-ui';
import React, { Fragment, useRef } from 'react';
import { AgendaEndpoints } from 'Models/Routes';
import { Row } from 'reactstrap';
import { SmartFileUploader } from 'Components/Form';
import ReCAPTCHA from 'react-google-recaptcha';

export class CommitteeApplicationModel {
  id?: string;
  committeeId?: string;
  committeePositionId?: string;
  configuration?: CommitteeApplicationConfigurationModel;
  captchaToken?: string;

  // Applicant
  firstName?: string;
  middleInitial?: string;
  lastName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  currentEmployer?: string;
  jobTitle?: string;
  yearsLived?: number;

  // Interests & Experience
  describeInterest?: string;
  summaryOfIssues?: string;
  summaryOfParticipation?: string;
  whatYouHaveToOffer?: string;
  conflictsOfInterest?: boolean;

  // Open Records and Public Information Act Acknowledgement
  allowPublicAccess?: boolean;

  // Attachments
  attachments?: DropdownItemModel[];
}

export class CommitteeApplicationConfigurationModel {
  id?: string;
  countyName?: string;

  // Applicant
  applicationNotice?: string;
  isMiddleInitialVisible?: boolean;
  isMiddleInitialRequired?: boolean;
  isCurrentEmployerVisible?: boolean;
  isCurrentEmployerRequired?: boolean;
  isJobTitleVisible?: boolean;
  isJobTitleRequired?: boolean;
  isYearsLivedVisible?: boolean;
  isYearsLivedRequired?: boolean;

  // Interests & Experience
  publicInformationNotice?: string;
  isDescribeInterestVisible?: boolean;
  isDescribeInterestRequired?: boolean;
  isSummaryOfIssuesVisible?: boolean;
  isSummaryOfIssuesRequired?: boolean;
  isSummaryOfParticipationVisible?: boolean;
  isSummaryOfParticipationRequired?: boolean;
  isWhatYouHaveToOfferVisible?: boolean;
  isWhatYouHaveToOfferRequired?: boolean;
  isConflictsOfInterestVisible?: boolean;
  isConflictsOfInterestRequired?: boolean;

  // Open Records and Public Information Act Acknowledgement
  openRecordsNotice?: string;
  isAllowPublicAccessVisible?: boolean;
  isAllowPublicAccessRequired?: boolean;

  // Attachments
  attachmentsNotice?: string;
  isAddAttachmentVisible?: boolean;
  isAddAttachmentRequired?: boolean;
}

export const PublicInformationNotice: React.FC = () => {
  return (
    <div className='public-information-notice'>OPEN RECORDS AND PUBLIC INFORMATION ACT ACKNOWLEDGEMENT</div>
  );
}

export const AddCommitteeApplication: React.FC = () => {
  const entityName = 'Committee Application';
  const [id] = useIdParam();
  const handleSuccess = useAddEditSuccessHandler(entityName, true, undefined, "Application sent to the committee.");
  const [data, loadingStatus] = useDataFetcher<CommitteeApplicationModel>(
    buildEndpoint(AgendaEndpoints.committees.controllerActionString("OpenPosition"), id),
    new CommitteeApplicationModel(),
    defaultDataTransform,
    CommitteeApplicationModel
  );
  const captchaRef = useRef<ReCAPTCHA>(null);
  const toast = useToasts();
  
  const handleSubmit = async () => {
    if(captchaRef !== undefined && captchaRef !== null &&
      captchaRef.current !== undefined && captchaRef.current !== null) {
      const token = captchaRef.current.getValue();
      captchaRef.current.reset();

      if(token === undefined || token === null || token === '') {
        toast.error("You must complete the captcha to submit this application.");
        return false;
      }
      data.captchaToken = token as string;
    }

    return true;
  }

  return (
    <AsyncLoadingSection loadingStatus={loadingStatus}>
      <TaskPageHeaderLayout
        title="Committee Application"
        formProviderProps={{
          formModel: data,
          endpoint: AgendaEndpoints.committees.controllerActionString("postApplication"),
          handleSuccess: handleSuccess,
          handleSubmitConfirmation: handleSubmit,
        }}
      >
        <AddEditActionItemContent data={data} captchaRef={captchaRef} />
      </TaskPageHeaderLayout>
    </AsyncLoadingSection>
  );
};


export const AddEditActionItemContent: React.FC<{
  data: CommitteeApplicationModel;
  captchaRef: React.RefObject<ReCAPTCHA>;
}> = ({data, captchaRef}) => {
  const { state } = useEnums();
  const docxFileType =
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    const pdfFileType = 'application/pdf';  

  return (
    <Fragment>
      <PageCard title="Applicant">
        <PageCardRowSection>
          <Render condition={data?.configuration?.applicationNotice !== undefined 
              && data?.configuration?.applicationNotice !== null}>
            <Row>
              <PublicInformationNotice />
              <DisplayBlock
                title="Notice to Applicants:"
                value={data?.configuration?.applicationNotice}
                width="100%"
              />
            </Row>
            <br />
          </Render>        
          <Row>
            <SmartInputBlock
              name="firstName"
              label="First Name"
              type={InputType.Text}
              colProps={ColumnLayout.Auto}
              required
            />
            <Render condition={data?.configuration?.isMiddleInitialVisible}>
              <SmartInputBlock
                name="middleInitial"
                label="MI"
                type={InputType.Text}
                colProps={ColumnLayout.XXS}
                required={data?.configuration?.isMiddleInitialRequired}
              />
            </Render>
            <SmartInputBlock
              name="lastName"
              label="Last Name"
              type={InputType.Text}
              colProps={ColumnLayout.Auto}
              required
            />
          </Row>
          <Row>          
            <SmartInputBlock
              name="address.line1"
              label="Address Line 1"
              type={InputType.Text}
              colProps={ColumnLayout.Large}
              required
            />
            </Row>
            <Row>
            <SmartInputBlock
              name="address.line2"
              label="Line 2"
              type={InputType.Text}
              colProps={ColumnLayout.Large}
            />
            </Row>
            <Row>
            <SmartInputBlock
              type={InputType.Text}
              name="address.city"
              label="City"
              required
            />
            <SmartInputBlock
              type={InputType.EnumDropdown}
              name="address.state"
              label="State"
              data={state}
              required
            />
            <SmartInputBlock
              name="address.zip"
              label="Zip Code"
              type={InputType.Text}
              required
            />
            </Row>
            <Row>
            <SmartInputBlock
              name="emailAddress"
              label="Email Address"
              type={InputType.Text}
              colProps={ColumnLayout.Med}
              required
            />
            <SmartInputBlock
              name="phoneNumber"
              label="Phone Number"
              type={InputType.PhoneNumber}
              colProps={ColumnLayout.Auto}
              required
            />
          </Row>        
          <Render condition={data?.configuration?.isCurrentEmployerVisible}>
            <Row>
            <SmartInputBlock
              name="currentEmployer"
              label="(Current) Employer"
              type={InputType.Text}
              colProps={ColumnLayout.Large}
              required={data?.configuration?.isCurrentEmployerRequired}
            />
            </Row>
          </Render>
          <Render condition={data?.configuration?.isJobTitleVisible}>
            <Row>
            <SmartInputBlock
              name="jobTitle"
              label="Job Title"
              type={InputType.Text}
              colProps={ColumnLayout.Large}
              required={data?.configuration?.isJobTitleRequired}
            />
            </Row>
          </Render>
          <Render condition={data?.configuration?.isYearsLivedVisible}>
            <Row>
            <SmartInputBlock
              name="yearsLived"
              label={"Years Lived in " + data?.configuration?.countyName + " County"}
              type={InputType.Number}
              colProps={ColumnLayout.XS}
              required={data?.configuration?.isYearsLivedRequired}
            />
            </Row>
          </Render>        
        </PageCardRowSection>
      </PageCard>
      <Render condition={(data?.configuration?.publicInformationNotice !== undefined 
        && data?.configuration?.publicInformationNotice !== null) 
          || data?.configuration?.isDescribeInterestVisible
          || data?.configuration?.isSummaryOfIssuesVisible
          || data?.configuration?.isSummaryOfParticipationVisible
          || data?.configuration?.isWhatYouHaveToOfferVisible
          || data?.configuration?.isConflictsOfInterestVisible }>
        <PageCard title="Interests & Experience">
          <PageCardRowSection>
            <Render condition={data?.configuration?.publicInformationNotice !== undefined 
              && data?.configuration?.publicInformationNotice !== null}>
              <Row>
                <PublicInformationNotice />
                <DisplayBlock
                  title="Notice to Applicants:"
                  value={data?.configuration?.publicInformationNotice}
                  width="100%"
                />
              </Row>
              <br />            
            </Render>
            <Row>
              <br />
              <Render condition={data?.configuration?.isDescribeInterestVisible}>
                <SmartInputBlock
                  name="describeInterest"
                  label="Describe your interest in serving on the requested Committee/Board:"
                  type={InputType.TextArea}
                  colProps={ColumnLayout.Full}
                  required={data?.configuration?.isDescribeInterestRequired}
                  className="large-text-area"
                />
              </Render>
              <Render condition={data?.configuration?.isSummaryOfIssuesVisible}>
                <SmartInputBlock
                  name="summaryOfIssues"
                  label="Provide a brief summary of the issues and/or topics you believe should be addressed by this Committee/Board this calendar year:"
                  type={InputType.TextArea}
                  colProps={ColumnLayout.Full}
                  required={data?.configuration?.isSummaryOfIssuesRequired}
                  className="large-text-area"
                />
              </Render>
              <Render condition={data?.configuration?.isSummaryOfParticipationVisible}>
                <SmartInputBlock
                  name="summaryOfParticipation"
                  label="Provide a brief Summary of your participation in any professional, public service, not for profit, or other organizations:"
                  type={InputType.TextArea}
                  colProps={ColumnLayout.Full}
                  required={data?.configuration?.isSummaryOfParticipationRequired}
                  className="large-text-area"
                />
              </Render>
              <Render condition={data?.configuration?.isWhatYouHaveToOfferVisible}>
                <SmartInputBlock
                  name="whatYouHaveToOffer"
                  label="What do you feel you have to offer this Board/Commission?"
                  type={InputType.TextArea}
                  colProps={ColumnLayout.Full}
                  required={data?.configuration?.isWhatYouHaveToOfferRequired}
                  className="large-text-area"
                />
              </Render>
              <Render condition={data?.configuration?.isConflictsOfInterestVisible}>
                <SmartInputBlock
                  name="conflictsOfInterest"
                  label="Do you have any Potential Conflicts Of Interest?"
                  type={InputType.YesNoBooleanRadioGroup}
                  colProps={ColumnLayout.Auto}
                  required={data?.configuration?.isConflictsOfInterestRequired}
                />
              </Render>
            </Row>
          </PageCardRowSection>
        </PageCard>
      </Render>
      <Render condition={(data?.configuration?.openRecordsNotice !== undefined 
        && data?.configuration?.openRecordsNotice !== null) 
          || data?.configuration?.isAllowPublicAccessVisible }>
        <PageCard title="Open Records & Public Information Act Acknowledgement">
          <PageCardRowSection>
            <Render condition={data?.configuration?.openRecordsNotice !== undefined 
              && data?.configuration?.openRecordsNotice !== null}>
              <Row>
                <PublicInformationNotice />
                <DisplayBlock
                  title="Notice to Applicants:"
                  value={data?.configuration?.openRecordsNotice}
                  width="100%"
                />              
              </Row>
              <br />
            </Render>
            <Row>            
              <Render condition={data?.configuration?.isAllowPublicAccessVisible}>
                <SmartInputBlock
                  name="allowPublicAccess"
                  label="Allow Public Access?"
                  type={InputType.YesNoBooleanRadioGroup}
                  colProps={ColumnLayout.Auto}
                  required={data?.configuration?.isAllowPublicAccessRequired}
                />
              </Render>
            </Row>
          </PageCardRowSection>
        </PageCard>
      </Render>
      <Render condition={(data?.configuration?.attachmentsNotice !== undefined 
        && data?.configuration?.attachmentsNotice !== null) 
          || data?.configuration?.isAddAttachmentVisible }>
        <PageCard title="Attachments">
          <PageCardRowSection>
            <Render condition={data?.configuration?.attachmentsNotice !== undefined 
              && data?.configuration?.attachmentsNotice !== null}>
              <Row>
                <PublicInformationNotice />
                <DisplayBlock
                  title="Notice to Applicants:"
                  value={data?.configuration?.attachmentsNotice}
                  width="100%"
                />              
              </Row>
              <br />
            </Render>
            <Row>
              <Render condition={data?.configuration?.isAddAttachmentVisible}>
                <SmartFileUploader
                  label="Upload Resume"
                  required={data?.configuration?.isAddAttachmentRequired}
                  requireAttachmentType={false}
                  fileEndpoint={AgendaEndpoints.committeeapplicationattachments.base}
                  isEdit={false}
                  acceptedFileTypes={[docxFileType, pdfFileType]}
                />
              </Render>
            </Row>
          </PageCardRowSection>
        </PageCard>
      </Render>
      <ReCAPTCHA
        sitekey={process.env.REACT_APP_SITE_KEY as string}
        ref={captchaRef}
      />
    </Fragment>
  );
};
